import { AnimatePresence, motion } from 'framer-motion'
import { Pause, Play, SkipBack, SkipForward } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { CheckInPhase } from '@/features/stadium/phases/CheckInPhase'
import { CompletePhase } from '@/features/stadium/phases/CompletePhase'
import { FinalMatchPhase } from '@/features/stadium/phases/FinalMatchPhase'
import { LivePhase } from '@/features/stadium/phases/LivePhase'
import { TeaserPhase } from '@/features/stadium/phases/TeaserPhase'
import { StadiumHeader } from '@/features/stadium/StadiumHeader'
import { StadiumTicker } from '@/features/stadium/StadiumTicker'
import type { StadiumPhase } from '@/features/stadium/derivePhase'
import '@/features/stadium/stadium.css'

import { DEMO_BRACKET, DEMO_EVENT } from '@/features/marketing/mockData/stadiumDemoFixture'

/**
 * Stadium demo player — drives the real Stadium phase components
 * from a controlled phase index instead of `useStadiumData`.
 *
 * Why reuse the real phase components rather than build mockups:
 *   - The point of the demo is to sell the *actual* performance
 *     (crossfade timing, spark-burst, bracket flips). Mockups
 *     would diverge from production the moment the real Stadium
 *     was tweaked.
 *   - The phase components already accept event + bracket props
 *     and don't care where the data came from, so swapping the
 *     polling hook for a fixture is a clean drop-in.
 *
 * Phase flow:
 *   TEASER → CHECK_IN → LIVE → FINAL_MATCH → COMPLETE → (loop)
 *
 * Controls:
 *   - Auto-advance every `AUTO_ADVANCE_MS` (default 7 s — enough
 *     time to take in each phase, not so much that visitors leave
 *     before completion)
 *   - Prev / next buttons jog ±1 phase and pause auto-advance
 *   - A row of dots below labels each phase + lets the visitor
 *     jump directly
 *   - Pause / play toggle for the auto-advance
 *
 * All phase components use the same `<AnimatePresence mode="wait">`
 * wrapper as the production Stadium, so the demo gets the real
 * 480 ms blur-crossfade between phases for free.
 */

const PHASES_IN_ORDER: StadiumPhase[] = [
  'TEASER',
  'CHECK_IN',
  'LIVE',
  'FINAL_MATCH',
  'COMPLETE',
]

const PHASE_LABELS: Record<StadiumPhase, string> = {
  TEASER: 'Pre-event',
  CHECK_IN: 'Check-in',
  AWAITING_LOCK_IN: 'Bracket draw',
  LIVE: 'Live bracket',
  FINAL_MATCH: 'Grand final',
  COMPLETE: 'Champion reveal',
  CANCELLED: 'Cancelled',
}

/** Default auto-advance cadence. Landing page wants this slow so
 *  visitors have time to read each phase; the new-owner tour
 *  overrides it with a faster value (~3.5s) so the demo loops
 *  twice in the time someone reads the surrounding copy. */
const DEFAULT_AUTO_ADVANCE_MS = 7_000

export interface StadiumDemoPlayerProps {
  /** Override the auto-advance cadence (ms). Defaults to 7000 for
   *  the landing page. The tour passes ~3500 so the cycle feels
   *  brisk enough to suit a 4-tab walkthrough. */
  autoAdvanceMs?: number
}

// 16:9 aspect — the canonical Stadium ratio. The inner frame is
// always sized at 16:9 of the document viewport width so:
//   - `vw` inside the frame resolves correctly (frame width =
//     document viewport width, so 13vw = 13% of frame too)
//   - On 16:9 viewports `vh` resolves correctly too (frame height
//     = document height)
//   - On ultrawide / portrait viewports, vh-sized elements appear
//     proportionally smaller within the frame, but layouts never
//     overflow because every clamp() lands inside its intended
//     range
//
// The frame is then visually scaled by `container width / viewport
// width` to fit inside the visible demo card. This is the only
// approach that works on viewports of arbitrary aspect — a fixed
// 1280-px frame breaks on ultrawides where clamp maxima
// (e.g. 540px avatars) outstrip the frame's interior width.
const FRAME_ASPECT = 16 / 9

export function StadiumDemoPlayer({
  autoAdvanceMs = DEFAULT_AUTO_ADVANCE_MS,
}: StadiumDemoPlayerProps = {}) {
  const [phaseIndex, setPhaseIndex] = useState(0)
  const [autoplay, setAutoplay] = useState(true)
  // recentlyCompleted is normally maintained by useStadiumData to
  // flag matches that flipped to COMPLETE since the previous poll.
  // The demo has no polling, so it's empty — the LIVE phase still
  // renders the bracket correctly without it.
  const recentlyCompleted = useRef<ReadonlySet<string>>(new Set()).current

  // Track BOTH the visible container width AND the document
  // viewport width. The frame size is derived from viewport width
  // (so `vw` resolves correctly inside the frame), and the scale
  // factor is `containerWidth / viewportWidth`.
  const outerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 1920 : window.innerWidth,
  )

  useLayoutEffect(() => {
    const el = outerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? el.clientWidth
      if (w > 0) setContainerWidth(w)
    })
    ro.observe(el)
    setContainerWidth(el.clientWidth)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    function onResize() {
      setViewportWidth(window.innerWidth)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Derived dimensions. Frame matches the viewport width so all
  // internal `vw` clamps resolve as the Stadium components were
  // designed to handle.
  const frameWidth = Math.max(1, viewportWidth)
  const frameHeight = frameWidth / FRAME_ASPECT
  const scale = containerWidth > 0 ? containerWidth / frameWidth : 1

  const phase = PHASES_IN_ORDER[phaseIndex]!

  // Auto-advance — only ticks while `autoplay` is on. Resets the
  // timer whenever the visitor jogs manually, so manual + auto
  // don't fight over the playhead.
  useEffect(() => {
    if (!autoplay) return
    const id = window.setTimeout(() => {
      setPhaseIndex((i) => (i + 1) % PHASES_IN_ORDER.length)
    }, autoAdvanceMs)
    return () => window.clearTimeout(id)
  }, [phaseIndex, autoplay, autoAdvanceMs])

  const goTo = useCallback((next: number) => {
    setAutoplay(false)
    setPhaseIndex(((next % PHASES_IN_ORDER.length) + PHASES_IN_ORDER.length) % PHASES_IN_ORDER.length)
  }, [])

  return (
    <div className="stadium-demo-player flex w-full flex-col overflow-hidden rounded-xl border border-border/40 shadow-2xl shadow-black/60">
      {/* Logo-fit override — the production Stadium uses
          object-cover on team avatars because real photos should
          fill the frame. The demo uses our square SVG hex-shield
          logos which need to letterbox inside the rounded
          container or their hex tips clip. Scoped to the demo via
          the .stadium-demo-player class so production behaviour is
          untouched. */}
      <style>{`
        .stadium-demo-player .stadium-root img {
          object-fit: contain !important;
          padding: 4%;
        }
      `}</style>

      {/* Outer container — observes its own width to compute the
          scale factor for the inner viewport-matched frame.
          `aspect-video` keeps the visible size 16:9 at any
          container width. */}
      <div
        ref={outerRef}
        className="relative aspect-video w-full overflow-hidden bg-[var(--tv-bg,#06060f)]"
      >
        {/* Inner frame — sized to match the document viewport width
            (with 16:9 height) so the Stadium's clamp(...,vw,...)
            sizing resolves the way the components were designed
            for. Visually scaled down by the wrapper to fit the
            demo card. `transform-origin: top left` anchors the
            scaled frame so its top-left corner aligns with the
            container's top-left. */}
        <div
          className="stadium-root absolute left-0 top-0 flex flex-col text-white"
          style={{
            width: frameWidth,
            height: frameHeight,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            backgroundColor: 'var(--tv-bg, #06060f)',
          }}
        >
          <StadiumHeader event={DEMO_EVENT} phase={phase} />

          <main className="relative flex-1 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={phase}
                initial={{ opacity: 0, filter: 'blur(8px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(8px)' }}
                transition={{ duration: 0.48, ease: [0.32, 0.72, 0, 1] }}
                className="absolute inset-0"
              >
                <PhaseBody phase={phase} recentlyCompleted={recentlyCompleted} />
              </motion.div>
            </AnimatePresence>
          </main>

          <StadiumTicker bracket={DEMO_BRACKET} event={DEMO_EVENT} />
        </div>

        {/* Watermark, top-right. Stays subtle but present so any
            screen capture carries the source. Sits OUTSIDE the
            scaled frame so its font-size doesn't shrink with the
            container — always reads at full document scale. */}
        <div className="pointer-events-none absolute right-3 top-3 z-10 select-none">
          <p
            className="font-display text-[10px] font-extrabold tracking-[0.25em] uppercase"
            style={{
              color: 'rgba(255,255,255,0.5)',
              textShadow: '0 0 8px rgba(0,0,0,0.85)',
            }}
          >
            e404 stadium · demo
          </p>
        </div>
      </div>

      {/* Player controls — chunky, distinctly NOT-broadcast styling
          so visitors don't confuse them for part of the Stadium. */}
      <div className="flex flex-col gap-3 border-t border-border bg-bg/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => goTo(phaseIndex - 1)}
            aria-label="Previous phase"
            className="inline-flex size-9 items-center justify-center rounded-md border border-border bg-surface/40 text-fg-muted transition-colors hover:text-fg"
          >
            <SkipBack className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setAutoplay((v) => !v)}
            aria-label={autoplay ? 'Pause auto-advance' : 'Resume auto-advance'}
            className="inline-flex size-9 items-center justify-center rounded-md border border-primary/40 bg-primary/15 text-primary transition-colors hover:bg-primary/25"
          >
            {autoplay ? (
              <Pause className="size-4" aria-hidden />
            ) : (
              <Play className="size-4" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => goTo(phaseIndex + 1)}
            aria-label="Next phase"
            className="inline-flex size-9 items-center justify-center rounded-md border border-border bg-surface/40 text-fg-muted transition-colors hover:text-fg"
          >
            <SkipForward className="size-4" aria-hidden />
          </button>
          <span className="font-display ml-2 text-[11px] font-bold tracking-[0.18em] text-fg-subtle uppercase">
            {PHASE_LABELS[phase]}
          </span>
        </div>

        {/* Phase dots — clickable jump points. Each represents one
            stage in the timeline, current one filled. */}
        <div className="flex items-center gap-2" role="tablist" aria-label="Phase timeline">
          {PHASES_IN_ORDER.map((p, i) => {
            const active = i === phaseIndex
            return (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={`Jump to ${PHASE_LABELS[p]}`}
                onClick={() => goTo(i)}
                className="group flex flex-col items-center gap-1"
              >
                <span
                  className="h-1.5 rounded-full transition-all"
                  style={{
                    width: active ? 28 : 14,
                    backgroundColor: active ? 'var(--color-primary, #3D6FFF)' : 'var(--color-border, #2a2a4d)',
                  }}
                />
                <span
                  className="font-display hidden text-[9px] font-bold tracking-[0.18em] uppercase sm:block"
                  style={{
                    color: active ? 'var(--color-primary, #3D6FFF)' : 'var(--color-fg-subtle, #6f6f8a)',
                  }}
                >
                  {PHASE_LABELS[p]}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function PhaseBody({
  phase,
  recentlyCompleted,
}: {
  phase: StadiumPhase
  recentlyCompleted: ReadonlySet<string>
}) {
  switch (phase) {
    case 'TEASER':
      return <TeaserPhase event={DEMO_EVENT} />
    case 'CHECK_IN':
      return <CheckInPhase event={DEMO_EVENT} />
    case 'LIVE':
      return (
        <LivePhase
          event={DEMO_EVENT}
          bracket={DEMO_BRACKET}
          recentlyCompleted={recentlyCompleted}
        />
      )
    case 'FINAL_MATCH':
      return (
        <FinalMatchPhase
          event={DEMO_EVENT}
          bracket={DEMO_BRACKET}
          recentlyCompleted={recentlyCompleted}
        />
      )
    case 'COMPLETE':
      return <CompletePhase event={DEMO_EVENT} bracket={DEMO_BRACKET} />
    default:
      return null
  }
}
